import * as React from 'react'

import { AdminIcon } from '@/modules/shared/components'
import { useClickOutside } from '@/app/hooks/useClickOutside'
import {
  CompanyFilterResult,
  CompanySettlementType,
  CompanyType,
  PackageOption,
  PackageProposalOption,
  SubscriptionExpirationDateOption,
  SuspendedOption,
  SynchronizationOption,
} from 'app/models/company/companyFilter'

export type { CompanyFilterResult }

export type CompanyFilterDropdownProps = {
  id?: string
  caret?: string
  findPlaceholder?: string
  initial: CompanyFilterResult
  onSelected: (result: CompanyFilterResult) => void
  translate: (text: any) => any
}


/**
 * Jeden filtr = tytuł sekcji, wszystkie wartości i klucz w wyniku. Stara wersja
 * powtarzała ten sam blok siedem razy - raz na `renderGroup`, raz na „tagi"
 * w zwiniętym pasku i raz w stanie komponentu.
 */
type FilterKey =
  | 'companyTypes'
  | 'companySettlementTypes'
  | 'synchronizationOptions'
  | 'packageOptions'
  | 'packageProposalOptions'
  | 'subscriptionExpirationDateOptions'
  | 'suspendedOptions'

type FilterGroup = {
  key: FilterKey
  /** Prefiks kluczy tłumaczeń, np. `companyFilterDropdown.companyType`. */
  translationKey: string
  /** Nazwy wariantów enuma - z nich powstaje klucz tłumaczenia pozycji. */
  names: Record<number, string>
  values: number[]
}

const GROUPS: FilterGroup[] = [
  {
    key: 'companyTypes',
    translationKey: 'companyFilterDropdown.companyType',
    names: CompanyType as any,
    values: [CompanyType.Accounting, CompanyType.Service, CompanyType.Normal],
  },
  {
    key: 'synchronizationOptions',
    translationKey: 'companyFilterDropdown.synchronizationOption',
    names: SynchronizationOption as any,
    values: [
      SynchronizationOption.NoSynchronization,
      SynchronizationOption.Updater,
      SynchronizationOption.VPN,
    ],
  },
  {
    key: 'packageOptions',
    translationKey: 'companyFilterDropdown.packageOption',
    names: PackageOption as any,
    values: [PackageOption.NoPackage, PackageOption.Package],
  },
  {
    key: 'packageProposalOptions',
    translationKey: 'companyFilterDropdown.packageProposalOption',
    names: PackageProposalOption as any,
    values: [PackageProposalOption.Propose],
  },
  {
    key: 'subscriptionExpirationDateOptions',
    translationKey: 'companyFilterDropdown.subscriptionExpirationDateOption',
    names: SubscriptionExpirationDateOption as any,
    values: [
      SubscriptionExpirationDateOption.None,
      SubscriptionExpirationDateOption.Defined,
    ],
  },
  {
    key: 'suspendedOptions',
    translationKey: 'companyFilterDropdown.suspendedOption',
    names: SuspendedOption as any,
    values: [
      SuspendedOption.None,
      SuspendedOption.Temporary,
      SuspendedOption.Permanently,
    ],
  },
  {
    key: 'companySettlementTypes',
    translationKey: 'companyFilterDropdown.companySettlementType',
    names: CompanySettlementType as any,
    values: [CompanySettlementType.Basic, CompanySettlementType.Pro],
  },
]

type Selection = Record<FilterKey, number[]>

const toSelection = (initial: CompanyFilterResult): Selection =>
  GROUPS.reduce(
    (acc, group) => ({ ...acc, [group.key]: [...(initial[group.key] ?? [])] }),
    {} as Selection,
  )

const Pill: React.FC<{
  onClick: () => void
  removable?: boolean
  children: React.ReactNode
}> = ({ onClick, removable, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`tw-flex tw-items-center tw-gap-1 tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs ${
      removable
        ? 'tw-border-primary tw-bg-primary/10 tw-text-primary'
        : 'tw-border-base-300 tw-bg-white hover:tw-border-primary hover:tw-text-primary'
    }`}
  >
    {children}
    {removable && <AdminIcon name="close" className="tw-h-3 tw-w-3" />}
  </button>
)

/**
 * Filtr listy firm - funkcyjny odpowiednik dawnej klasy na bootstrapowym `Dropdown`.
 *
 * Zachowanie zostaje: wyszukiwarka w pasku, sekcje z pigułkami niewybranych wartości,
 * wybrane jako odznaczalne tagi, a wynik leci do rodzica dopiero przy zamknięciu panelu.
 */
export const CompanyFilterDropdown: React.FC<CompanyFilterDropdownProps> = ({
  id,
  findPlaceholder,
  initial,
  onSelected,
  translate,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState(initial.searchValue ?? '')
  const [subcompanies, setSubcompanies] = React.useState(
    initial.subcompanies ?? false,
  )
  const [selection, setSelection] = React.useState<Selection>(() =>
    toSelection(initial),
  )

  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const label = (group: FilterGroup, value: number) =>
    translate(`${group.translationKey}.${group.names[value]}`)

  const apply = (
    next: Selection = selection,
    nextSubcompanies: boolean = subcompanies,
  ) =>
    onSelected({
      searchValue,
      subcompanies: nextSubcompanies,
      companyTypes: next.companyTypes,
      companySettlementTypes: next.companySettlementTypes,
      packageOptions: next.packageOptions,
      packageProposalOptions: next.packageProposalOptions,
      synchronizationOptions: next.synchronizationOptions,
      subscriptionExpirationDateOptions: next.subscriptionExpirationDateOptions,
      suspendedOptions: next.suspendedOptions,
    })

  // Rodzic dostaje wynik dopiero przy zamknięciu panelu, tak jak w starej wersji -
  // inaczej każde kliknięcie pigułki odpalałoby zapytanie o listę firm.
  const close = () => {
    setIsOpen(false)
    apply()
  }

  useClickOutside({
    ref: containerRef,
    handler: () => {
      if (isOpen) close()
    },
  })

  const toggleValue = (key: FilterKey, value: number) => {
    const next: Selection = {
      ...selection,
      [key]: selection[key].includes(value)
        ? selection[key].filter((item) => item !== value)
        : [...selection[key], value],
    }
    setSelection(next)
    // Zdjęcie tagu przy zwiniętym panelu nie ma czego zamykać, więc filtruje od razu.
    if (!isOpen) apply(next)
  }

  const selectedTags = GROUPS.flatMap((group) =>
    selection[group.key].map((value) => ({ group, value })),
  )

  return (
    <div ref={containerRef} id={id} className="tw-relative tw-w-full">
      <div className="tw-flex tw-min-h-[38px] tw-flex-wrap tw-items-center tw-gap-2 tw-py-1">
        {selectedTags.map(({ group, value }) => (
          <Pill
            key={`${group.key}-${value}`}
            removable
            onClick={() => toggleValue(group.key, value)}
          >
            {label(group, value)}
          </Pill>
        ))}
        {subcompanies && (
          <Pill
            removable
            onClick={() => {
              setSubcompanies(false)
              if (!isOpen) apply(selection, false)
            }}
          >
            {translate('companyFilterDropdown.search.filter-subcompanies')}
          </Pill>
        )}
        <input
          ref={inputRef}
          className="tw-min-w-[200px] tw-flex-1 tw-border-0 tw-bg-transparent tw-py-1 tw-text-sm tw-outline-none"
          placeholder={findPlaceholder}
          value={searchValue}
          onFocus={() => setIsOpen(true)}
          onChange={(event) => setSearchValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') close()
            if (event.key === 'Escape') close()
          }}
        />
      </div>

      {isOpen && (
        <div className="tw-absolute tw-left-0 tw-right-0 tw-top-full tw-z-20 tw-max-h-[420px] tw-overflow-y-auto tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-shadow-lg">
          {searchValue !== '' && (
            <div className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3">
              <p className="tw-mb-2 tw-text-xs tw-text-content-base/70">
                {translate('companyFilterDropdown.search.title')}
              </p>
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <Pill onClick={() => inputRef.current?.focus()}>
                  {searchValue}
                </Pill>
                {!subcompanies && (
                  <Pill onClick={() => setSubcompanies(true)}>
                    {translate(
                      'companyFilterDropdown.search.filter-subcompanies',
                    )}
                  </Pill>
                )}
              </div>
            </div>
          )}

          {GROUPS.map((group) => {
            const available = group.values.filter(
              (value) => !selection[group.key].includes(value),
            )
            if (available.length === 0) return null

            return (
              <div
                key={group.key}
                className="tw-border-b tw-border-base-300 tw-px-4 tw-py-3 last:tw-border-b-0"
              >
                <p className="tw-mb-2 tw-text-xs tw-text-content-base/70">
                  {translate(`${group.translationKey}.title`)}
                </p>
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  {available.map((value) => (
                    <Pill
                      key={value}
                      onClick={() => toggleValue(group.key, value)}
                    >
                      {label(group, value)}
                    </Pill>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
